'use client';

import { useMemo, useCallback } from 'react';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Tabs, { type TabOption } from '../tabs';
import Host from '../host';
import { eventSeriesPath } from '../../utils/slugify';
import type { EventSeries } from '../../types/EventSeries';

const useStyles = makeStyles()((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(2, 0),
    [theme.breakpoints.down('sm')]: {
      flexFlow: 'column',
      alignItems: 'flex-start',
    },
  },
  headerLeft: {
    flexGrow: 1,
    paddingRight: theme.spacing(2),
  },
  title: {
    margin: 0,
  },
  titleLink: {
    color: 'rgba(0, 0, 0, 0.87)',
    textDecoration: 'none',
  },
  tabs: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  hostWrapper: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0.5, 0),
    flexWrap: 'wrap',
  },
  hostBy: {
    marginRight: theme.spacing(0.5),
  },
  host: {
    '& > *': {
      marginBottom: 2,
    },
  },
}));

type Props = {
  eventSeries: EventSeries;
  tab: string;
};

const EventSeriesHeader = ({ eventSeries, tab }: Props) => {
  const { classes } = useStyles();
  const router = useRouter();

  const tabOptions = useMemo(() => {
    const tabs: TabOption[] = [
      {
        label: 'Overview',
        value: 'overview',
        component: "a",
        href: eventSeriesPath(eventSeries.id, eventSeries.name)
      }
    ];
    if (eventSeries.permissions?.editable) {
      tabs.push({
        label: 'Schedule',
        value: 'schedule',
        component: "a",
        href: `${eventSeriesPath(eventSeries.id, eventSeries.name)}/schedule`
      });
      tabs.push({
        label: 'Settings',
        value: 'settings',
        component: "a",
        href: `${eventSeriesPath(eventSeries.id, eventSeries.name)}/settings`
      });
    }
    return tabs;
  }, [eventSeries]);

  const handleTabChange = useCallback((_e: React.SyntheticEvent, newTab: string) => {
    _e.preventDefault();
    if (newTab === 'overview') {
      router.push(eventSeriesPath(eventSeries.id, eventSeries.name));
    } else if (newTab === 'schedule') {
      router.push(`${eventSeriesPath(eventSeries.id, eventSeries.name)}/schedule`);
    } else if (newTab === 'settings') {
      router.push(`${eventSeriesPath(eventSeries.id, eventSeries.name)}/settings`);
    }
  }, [router, eventSeries]);

  return (
    <div className={classes.header}>
      <div className={classes.headerLeft}>
        <Typography variant="h1" className={classes.title}>
          <Link
            href={eventSeriesPath(eventSeries.id, eventSeries.name)}
            className={classes.titleLink}
          >
              {eventSeries.name}
          </Link>
        </Typography>
        <div className={classes.hostWrapper}>
          <span className={classes.hostBy}>Series by</span>
          <Host
            event={eventSeries}
            className={classes.host}
          />
        </div>
      </div>
      {tabOptions.length > 1 && (
        <Tabs
          options={tabOptions}
          tab={tab}
          onTabChange={handleTabChange}
          className={classes.tabs}
        />
      )}
    </div>
  );
}

export default EventSeriesHeader;
